import { API_ENDPOINTS } from '../constants/api';
import { BASE_URL } from '../constants/config';

// Server-side data fetching utilities with ISR support

export async function fetchCars(params) {
  try {
    const searchParams = new URLSearchParams();
    
    // Add all parameters
    if (params.lang) searchParams.set('lang', params.lang);
    if (params.per_page) searchParams.set('per_page', params.per_page);
    if (params.page) searchParams.set('page', params.page);
    if (params.currency) searchParams.set('currency', params.currency);
    
    // Handle array parameters
    if (params.brand_ids?.length > 0) {
      params.brand_ids.forEach(id => searchParams.append('brand_ids[]', id));
    }
    if (params.body_type_ids?.length > 0) {
      params.body_type_ids.forEach(id => searchParams.append('body_type_ids[]', id));
    }
    if (params.color_ids?.length > 0) {
      params.color_ids.forEach(id => searchParams.append('color_ids[]', id));
    }
    if (params.transmission?.length > 0) {
      params.transmission.forEach(t => searchParams.append('transmission[]', t));
    }
    
    // Handle single parameters
    if (params.fuel_type) searchParams.set('fuel_type', params.fuel_type);
    if (params.search) searchParams.set('search', params.search);
    if (params.sort_by) searchParams.set('sort_by', params.sort_by);
    
    const url = `${API_ENDPOINTS.CAR_LIST}?${searchParams.toString()}`;
    //console.log('Fetching cars from:', url);
    
    // Make the API call
    const response = await fetch(url, {
      next: { revalidate: 30 },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    //console.log('Cars API response:', result);
    return result;
  } catch (error) {
    console.error('Error fetching cars:', error);
    return { data: [], error: error.message };
  }
}

export async function fetchCarDetails(slug, params = {}) {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `${BASE_URL}api/car/${slug}${queryString ? `?${queryString}` : ''}`;
    
    //console.log('Fetching car details from:', url);
    
    const response = await fetch(url, {
      next: { revalidate: 30 },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    //console.log('Car details API response:', result);
    return result;
  } catch (error) {
    console.error('Error fetching car details:', error);
    return { data: null, error: error.message };
  }
}

export async function fetchFilters(params = {}) {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_ENDPOINTS.FILTERS}${queryString ? `?${queryString}` : ''}`;
    
    //console.log('Fetching filters from:', url);
    
    const response = await fetch(url, {
      next: { revalidate: 30 },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      console.error(`Filters API error: ${response.status} ${response.statusText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    //console.log('Filters API response:', result);
    
    // Check if the response has the expected structure
    if (!result.data && !result.brands && !result.bodyTypes) {
      console.warn('Unexpected filters response structure:', result);
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching filters:', error);
    return { 
      data: {
        brands: [],
        bodyTypes: [],
        colors: [],
        fuelTypes: [],
        transmission: []
      }, 
      error: error.message 
    };
  }
}

export async function fetchPopularCars(params = {}) {
  //console.log("params =====> ", params)
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_ENDPOINTS.POPULAR_CARS}${queryString ? `?${queryString}` : ''}`;
    
    //console.log('Fetching popular cars from:', url);
    
    const response = await fetch(url, {
      next: { revalidate: 30 },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      console.error(`Popular cars API error: ${response.status} ${response.statusText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    //console.log('Popular cars API response:', result);
    
    // Check if the response has data
    if (!result.data && !Array.isArray(result)) {
      console.warn('Unexpected popular cars response structure:', result);
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching popular cars:', error);
    return { data: [], error: error.message };
  }
}

export async function fetchHomeData(params = {}) {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_ENDPOINTS.HOME}${queryString ? `?${queryString}` : ''}`;

    //console.log('Fetching home data from:', url);

    const response = await fetch(url, {
      next: { revalidate: 30 },
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      console.error(`Home API error: ${response.status} ${response.statusText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    //console.log('Home data API response:', result);
    return result;
  } catch (error) {
    console.error('Error fetching home data:', error);
    return { data: null, error: error.message };
  }
}

// Helper function to fetch all car slugs for static generation
export async function fetchAllCarSlugs() {
  try {
    const url = `${API_ENDPOINTS.CAR_LIST}?per_page=100`;
    //console.log('Fetching car slugs from:', url);
    
    const response = await fetch(url, {
      next: { revalidate: 30 },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    //console.log('Car slugs API response:', data);
    return data.data || data || [];
  } catch (error) {
    console.error('Error fetching car slugs:', error);
    return [];
  }
}

// Debug function to test all endpoints
export async function debugAllEndpoints() {
  //console.log('=== DEBUG: Testing all API endpoints ===');
  
  try {
    //console.log('API_ENDPOINTS:', API_ENDPOINTS);
    
    // Test each endpoint
    const results = await Promise.allSettled([
      fetchCars({ lang: 'en', per_page: 5, page: 1, currency: 'usd' }),
      fetchFilters({ lang: 'en' }),
      fetchPopularCars({ lang: 'en' }),
      fetchHomeData({ lang: 'en' })
    ]);
    
    //console.log('Cars result:', results[0]);
    //console.log('Filters result:', results[1]);
    //console.log('Popular cars result:', results[2]);
    //console.log('Home data result:', results[3]);
    
  } catch (error) {
    console.error('Debug error:', error);
  }
  
  //console.log('=== END DEBUG ===');
}